import { useState, useRef } from 'react';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import { useRecoilValue } from 'recoil';

import TranslationText from '@Components/Utilities/TranslationText';
import TextField from '@Components/Shared/Controls/TextField'
import atoms from '@Components/Utilities/SandhillsAtoms';
import { insertURLParameter } from '@Components/Utilities/SandhillsUtility';
import ValidationErrors from '@Components/Shared/ValidationErrors';
import { ValidationRules, ruleRunner, run } from '@Components/Utilities/Validation';

export const CharterCompanySearch = (props) => {
    const inputReferences = useRef({});
    const [formData, setFormData] = useState({ Distance: '', Country: '' });
    const distanceOptions = [10, 20, 50, 150, 200];
    const [validationErrors, setValidationErrors] = useState({});
    const countryOptions = props.GetCountryDropDown || [];
    const routes = useRecoilValue(atoms.routes);
    const [error, setError] = useState('');

    const onAirportChange = (e) => {
        let id = e.target.id;
        let value = e.target.value;
        setFormData(previousState => {
            return { ...previousState, [id]: { AirportCodeDisplay: value } };
        });
    }

    const setDistance = (e) => {
        setFormData(previousState => {
            return { ...previousState, Distance: e.target.value };
        });
    }

    const setCountry = (e) => {
        setFormData(previousState => {
            return { ...previousState, Country: e.target.value };
        });
    }

    const getAutoCompleteUrl = (e, url) => {
        return url;
    };

    const getAutoCompleteData = (e) => {
        return {
            text: e.target.value
        };
    };

    const companySearch = (e) => {
        e.preventDefault();

        let url = props.searchURL;

        if ((formData.CharterSearchCityAirportCode?.AirportCodeID > 0 || formData.CharterSearchCityAirportCode?.AirportCityID > 0) || formData.Distance !== '') {
            let errors = run(formData, [
                ruleRunner('Distance', translator.translate('sandhills.distance'), ValidationRules.required),
                ruleRunner('CharterSearchCityAirportCode', translator.translate('sandhills.FromCityAirportCode'), ValidationRules.required),
            ]);

            setValidationErrors(errors);

            if (formData.Distance !== '') {
                if (formData.CharterSearchCityAirportCode?.AirportCityID > 0) {
                    url = insertURLParameter(url, 'CityID', formData.CharterSearchCityAirportCode.AirportCityID);
                }
                else if (formData.CharterSearchCityAirportCode?.AirportCodeID > 0) {
                    url = insertURLParameter(url, 'AirportID', formData.CharterSearchCityAirportCode.AirportCodeID);
                }
                else {
                    setError('Invalid City/Airport. Select value from dropdown or remove airport and distance.');
                    return;
                }

                url = insertURLParameter(url, 'Distance', formData.Distance);
            }
        } else if (formData.Country !== '') {
            url = insertURLParameter(url, 'Country', formData.Country.Value);
        }

        window.location.href = url;
    };

    return (
        <>
            {error && <div className='error' style={{ fontSize: '12px' }}>{error}</div>}
            <div className='form-row'>
                <div className='RAC-input-container'>
                    <div className='boxed-input airport-selector'>
                        <FlightTakeoffIcon className='material-icon' />
                        <TextField
                            id='CharterSearchCityAirportCode'
                            name='CharterSearchCityAirportCode'
                            label={<TranslationText tKey='sandhills.tradesites.mvc.cityairportfrom' />}
                            value={formData.CharterSearchCityAirportCode?.AirportCodeDisplay}
                            type='text'
                            required
                            ref={(element) => inputReferences.current[`CharterSearchCityAirportCode`] = element}
                            updateField={(value, airportcodeid, cityid) => setFormData(previousState => {
                                return { ...previousState, CharterSearchCityAirportCode: { AirportCodeID: airportcodeid, AirportCityID: cityid, AirportCodeDisplay: value } };
                            })}
                            onFieldChanged={onAirportChange}
                            autoComplete={{
                                ajaxUrl: (e) => getAutoCompleteUrl(e, routes.GetCityAirportCodeAutoComplete),
                                postData: (e) => getAutoCompleteData(e)
                            }}
                            useMUI
                            InputProps={{
                                disableUnderline: true
                            }}
                        />
                    </div>
                    <span className='error'>{validationErrors.CharterSearchCityAirportCode}</span>
                </div>
                <div className='RAC-input-container aircraft-selector'>
                    <div className='boxed-input'>
                        <FormControl fullWidth>
                            <InputLabel htmlFor="distance-select-label" className='shrink-label'><TranslationText tKey='sandhills.tradesites.mvc.distancefromairport' /></InputLabel>
                            <Select
                                id="distance-select"
                                value={formData.Distance}
                                onChange={setDistance}
                                inputProps={{
                                    id: 'distance-select-label'
                                }}
                            >
                                <MenuItem value=""><em><TranslationText tKey='sandhills.crm.dscrm.none' /></em></MenuItem>
                                {distanceOptions.map((distance) => (
                                    <MenuItem key={`rdk-${distance}`} value={distance}>{distance} {props.IsMetric ? translator.translate('sandhills.tradepubwebsite.mvc.kilometersabrv') : translator.translate('sandhills.tradepubwebsite.mvc.miles')}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>
                    <span className='error'>{validationErrors.Distance}</span>
                </div>
                <div className="divider-text">
                    - OR -
                </div>
                <div className='RAC-input-container country-selector'>
                    <div className='boxed-input'>
                        <FormControl fullWidth>
                            <InputLabel id="country-select-label" className='shrink-label'><TranslationText tKey='sandhills.country' /></InputLabel>
                            <Select
                                labelId="country-select-label"
                                id="country-select"
                                value={formData.Country}
                                label={translator.translate('sandhills.country')}
                                onChange={setCountry}
                            >
                                {countryOptions.map((country) => (
                                    <MenuItem key={`rck-${country.Text}`} value={country}>{country.Text}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>
                    <span className='error'>{validationErrors.Country}</span>
                </div>
                <input className='ts-button submit-button button' type='submit' id='request-a-charter-submit-button' name='request-a-charter-submit-button' value={translator.translate('sandhills.tradepubwebsite.mvc.submit')} onClick={companySearch} />
            </div>
        </>
    );
}

export default CharterCompanySearch;